import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CallAnalysisDTO,
  CallAnalysisAgentPerformance,
  CallAnalysisComplianceCheck,
  CallAnalysisIntent,
  CallAnalysisSemanticExtraction,
  CallAnalysisTopic,
  CallAnalysisExecutiveSynthesis,
  CallAnalysisPassMetric,
  TranscriptSegment,
} from '@marnic/shared';
import { relativeTime } from '../lib/utils';
import { flipCallAnalysisAttribution, retryCallAnalysis, useCallAnalysis } from './useCallAnalysis';
import { RecordingDiagnosticButton } from './CallRecordingPlayer';
import { SKIP_LABEL } from './skip-labels';
import { useAuthStore } from '../store/auth';
import {
  EvidenceProvider,
  ExecutiveSummaryCard,
  DealSnapshotCard,
  NextActionsCard,
  OutcomeStrip,
  SentimentStrip,
  IntentTopicChips,
  SemanticFactsList,
  AgentPerformanceCard,
  ComplianceCard,
  PassMetricsTable,
  TranscriptDrawer,
} from './analysis';

interface Props {
  callId: string;
  onApplyDisposition?: (code: string) => void;
}

// SKIP_LABEL moved to ./skip-labels.ts (shared with AiJobsSection).

function RetryButton({ onRetry, retrying, label = 'Retry' }: { onRetry: () => void; retrying: boolean; label?: string }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onRetry}
      disabled={retrying}
      className="btn-secondary !px-2 !py-0.5 text-[11px] disabled:opacity-40"
    >
      {retrying ? 'Starting…' : label}
    </button>
  );
}

function StatusHeader({
  analysis,
  onRetry,
  retrying,
  onFlip,
  flipping,
  canFlip,
}: {
  analysis: CallAnalysisDTO;
  onRetry: () => void;
  retrying: boolean;
  onFlip?: () => void;
  flipping?: boolean;
  canFlip?: boolean;
}): JSX.Element {
  if (analysis.status === 'pending' || analysis.status === 'running') {
    return <div className="text-xs text-ink-500 italic">Analysis in progress…</div>;
  }
  if (analysis.status === 'skipped') {
    const code = analysis.lastError ?? '';
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-ink-500 italic">{SKIP_LABEL[code] ?? `Skipped (${code})`}</span>
        <RetryButton onRetry={onRetry} retrying={retrying} label="Re-analyze" />
      </div>
    );
  }
  if (analysis.status === 'failed') {
    return (
      <div className="flex items-center gap-2 text-xs text-red-700">
        <span>Analysis failed{analysis.lastError ? `: ${analysis.lastError.split(':')[0]}` : ''}</span>
        <RetryButton onRetry={onRetry} retrying={retrying} label="Retry" />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-ink-500">
        Updated {analysis.completedAt ? relativeTime(analysis.completedAt) : '—'}
      </span>
      <RetryButton onRetry={onRetry} retrying={retrying} label="Re-analyze" />
      {canFlip && onFlip && (
        <button
          type="button"
          onClick={onFlip}
          disabled={flipping}
          className="btn-secondary !px-2 !py-0.5 text-[11px] disabled:opacity-40"
          title="Swap agent ↔ prospect labels on the transcript and re-run the LLM analysis. Use when AI flipped speakers."
        >
          {flipping ? 'Flipping…' : 'Flip speakers'}
        </button>
      )}
    </div>
  );
}

export function CallAnalysisPanel({ callId, onApplyDisposition }: Props): JSX.Element | null {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [flipping, setFlipping] = useState(false);
  const [flipError, setFlipError] = useState<string | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnostic, setDiagnostic] = useState<string | null>(null);

  const query = useCallAnalysis(callId);

  if (query.isLoading) {
    return (
      <div className="rounded-md border border-ink-200 bg-white p-3 text-xs text-ink-500">
        Loading analysis…
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="rounded-md border border-ink-200 bg-white p-3 text-xs text-red-600">
        Could not load analysis.
      </div>
    );
  }
  if (!query.data || query.data.state === 'none') {
    return (
      <div className="rounded-md border border-dashed border-ink-200 bg-white p-3 text-[11px] text-ink-500 italic">
        No AI analysis available for this call.
      </div>
    );
  }

  const analysis = query.data.analysis;
  const segments = (analysis.transcriptSegments as TranscriptSegment[] | null) ?? [];
  const semantic = analysis.semanticExtraction as CallAnalysisSemanticExtraction | null;
  const performance = analysis.agentPerformance as CallAnalysisAgentPerformance | null;
  const compliance = analysis.complianceCheck as CallAnalysisComplianceCheck | null;
  const synthesis = analysis.executiveSynthesis as CallAnalysisExecutiveSynthesis | null;
  const passMetrics = analysis.passMetrics as Record<string, CallAnalysisPassMetric> | null;

  const handleRetry = async (): Promise<void> => {
    setRetrying(true);
    setRetryError(null);
    try {
      await retryCallAnalysis(callId);
      void qc.invalidateQueries({ queryKey: ['call', callId, 'analysis'] });
    } catch (err) {
      const code = (err as { code?: string }).code;
      setRetryError(code ?? 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const handleFlip = async (): Promise<void> => {
    if (!window.confirm('Swap Agent ↔ Prospect labels on the transcript and re-run the AI analysis? Use this when the AI mistook the agent for the prospect (or vice versa).')) {
      return;
    }
    setFlipping(true);
    setFlipError(null);
    try {
      await flipCallAnalysisAttribution(callId);
      void qc.invalidateQueries({ queryKey: ['call', callId, 'analysis'] });
    } catch (err) {
      const code = (err as { code?: string }).code;
      setFlipError(code ?? 'Flip failed');
    } finally {
      setFlipping(false);
    }
  };

  const showBody = analysis.status === 'succeeded';
  const canSeeMetrics = user?.role === 'admin';
  const canDiagnose = user?.role === 'admin' || user?.role === 'supervisor';
  const canFlip = (user?.role === 'admin' || user?.role === 'supervisor') && analysis.status === 'succeeded' && segments.length > 0;
  const isRecordingFailure =
    analysis.status === 'failed' &&
    !!analysis.lastError &&
    (analysis.lastError.startsWith('RECORDING_FETCH_') ||
      analysis.lastError.startsWith('RECORDING_MEDIA_AUTH_FAILED'));

  return (
    <EvidenceProvider>
      <div className="rounded-md border border-ink-200 bg-white p-3 space-y-2 text-sm">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] uppercase tracking-wide text-ink-500">AI Insights</div>
          <StatusHeader
            analysis={analysis}
            onRetry={() => void handleRetry()}
            retrying={retrying}
            onFlip={canFlip ? () => void handleFlip() : undefined}
            flipping={flipping}
            canFlip={canFlip}
          />
        </div>
        {retryError && <div className="text-xs text-red-600">{retryError}</div>}
        {flipError && <div className="text-xs text-red-600">{flipError}</div>}
        {canDiagnose && isRecordingFailure && (
          <RecordingDiagnosticButton
            callId={callId}
            diagnosing={diagnosing}
            diagnostic={diagnostic}
            onDiagnosing={setDiagnosing}
            onDiagnostic={setDiagnostic}
          />
        )}

        {showBody && (
          <>
            <ExecutiveSummaryCard synthesis={synthesis} fallbackSummary={analysis.summary} />

            <OutcomeStrip
              callOutcome={synthesis?.callOutcome}
              dataQuality={synthesis?.dataQuality}
              callType={performance?.callType}
              suggestedDisposition={analysis.suggestedDisposition}
              onApplyDisposition={onApplyDisposition}
            />

            <SentimentStrip analysis={analysis} />
            <IntentTopicChips
              intents={analysis.intents as CallAnalysisIntent[] | null}
              topics={analysis.topics as CallAnalysisTopic[] | null}
            />

            <DealSnapshotCard snapshot={synthesis?.dealSnapshot} />

            <NextActionsCard
              crmTask={synthesis?.crmTask}
              executiveAction={synthesis?.executiveAction}
              riskFlags={synthesis?.riskFlags}
            />

            <SemanticFactsList semantic={semantic} />
            <AgentPerformanceCard performance={performance} />
            <ComplianceCard compliance={compliance} />

            {canSeeMetrics && <PassMetricsTable metrics={passMetrics} />}

            <TranscriptDrawer segments={segments} />
          </>
        )}
      </div>
    </EvidenceProvider>
  );
}
